import "./styles.scss";
import "react-swipe-to-delete-component/dist/swipe-to-delete.css";

import { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SwipeToDelete from "react-swipe-to-delete-component";
import { dispatch, Events } from "../../../redux/dispatch";

import {
  AppContainer,
  Button,
  PoolResults,
  CreatePoolWizard,
} from "../../components";
import { useNavigate } from "react-router-dom";

import { usePool } from "../../../hooks/usePool";

import addMatchImg from "../../../assets/add-white.webp";

import { formatToMexicoUTC, getDayInMexico } from "../../../utils";

export const CreatePool = () => {
  const { createMainPool, updateMainPool, getMainPool } = usePool();
  const mainPool = useSelector((state) => state.mainPool);

  const [matches, setMatches] = useState([]);
  const [matchesWithResults, setMachesWithResults] = useState([]);
  const [showCreatePoolWizard, setShowCreatePoolWizard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startAt, setStartAt] = useState(null);

  const navigation = useNavigate();

  useEffect(() => {
    if (mainPool) {
      setMachesWithResults(mainPool.matches);
    }
  }, [mainPool]);

  const runCreateMainPool = async () => {
    setIsLoading(true);

    await createMainPool(matches, startAt);
    await getMainPool();
    dispatch(Events.SHOW_ALERT, {
      message: "¡Quiniela creada con éxito!",
      type: "SUCCESS",
    });
    navigation("/home");
  };

  function getClosestMatch(list, now = new Date()) {
    if (!list.length) return null;

    const nowMs = now.getTime();

    return list.reduce((closest, current) => {
      const currentTime = new Date(current.time).getTime();
      const closestTime = new Date(closest.time).getTime();

      if (Number.isNaN(currentTime)) return closest;
      if (Number.isNaN(closestTime)) return current;

      const currentDiff = Math.abs(currentTime - nowMs);
      const closestDiff = Math.abs(closestTime - nowMs);

      return currentDiff < closestDiff ? current : closest;
    });
  }

  const existsById = (id, list) => list.some((item) => item.id === id);

  const createMatch = (fixture) => {
    const match = {
      local: fixture.local,
      away: fixture.away,
      time: fixture.time,
      id: fixture.id,
    };
    setMatches((prevMatches) => {
      if (existsById(match.id, prevMatches)) return prevMatches;

      const updatedMatches = [...prevMatches, match];
      const closestMatch = getClosestMatch(updatedMatches);
      setStartAt(closestMatch ? closestMatch.time : null);

      return updatedMatches;
    });
  };

  const createPool = useCallback(() => {
    return matches.map((match, index) => (
      <SwipeToDelete
        key={`match-${index}`}
        classNameTag='preview-match-swipe w-[100%] mt-3'
        background={null}
        onDelete={() => onDeleteMatch(match.id)}>
        <div className='flex flex-col justify-center items-center w-full py-[2px]'>
          <section className='flex justify-between items-center w-[100%]'>
            <section className='flex-1 flex mr-2 items-center'>
              <span className='text-[12px] flex-1 text-center text-right mr-4'>
                {match.local.name}
              </span>
              <img
                src={match.local.logo}
                alt={match.local.name}
                width={26}
                className='object-contain'
              />
            </section>
            <span className='text-[12px]'>vs</span>
            <section className='flex-1 flex ml-2 items-center'>
              <img
                src={match.away.logo}
                alt={match.away.name}
                width={26}
                className='object-contain'
              />
              <span className='text-[12px] flex-1 text-left ml-4'>
                {match.away.name}
              </span>
            </section>
          </section>
          {match.time && (
            <section>
              <span className='text-xs opacity-50 text-[10px]'>
                {getDayInMexico(match.time)}
              </span>
              <span className='text-xs opacity-50 ml-1 text-[10px]'>
                {formatToMexicoUTC(match.time)}
              </span>
            </section>
          )}
          <div className='w-full bg-gray-600 h-[1px] mt-2' />
        </div>
      </SwipeToDelete>
    ));
  }, [matches]);

  const poolResultUpdate = (dataResult) => {
    let newMatches = [...matchesWithResults];

    newMatches[dataResult.index] = {
      ...newMatches[dataResult.index],
      result: dataResult.result,
    };

    setMachesWithResults(newMatches);
  };

  const createPoolResults = () =>
    [...mainPool.matches].map((match, index) => (
      <PoolResults
        key={index}
        match={match}
        index={index}
        onUpdate={poolResultUpdate}
        disable={match.status && match.status !== "AVAILABLE"}
      />
    ));

  const updatePool = async () => {
    setIsLoading(true);
    await updateMainPool(matchesWithResults);
    navigation("/home");
  };

  const onDeleteMatch = (matchId) => {
    setMatches(matches.filter((matches) => matches.id !== matchId));
  };

  return (
    <>
      {showCreatePoolWizard && (
        <CreatePoolWizard
          onClose={() => setShowCreatePoolWizard(false)}
          onAddMatch={createMatch}
        />
      )}
      <AppContainer verifyAuth={true} isLoading={isLoading}>
        <div className='flex flex-col items-center w-[100%] h-full z-20 h-full'>
          <section className='flex flex-col items-center mt-5'>
            <span className='text-2xl'>
              {!mainPool ? "Crea tu quiniela" : "Actualizar resultados"}
            </span>

            {!mainPool && matches.length > 0 && (
              <span className='text-[12px] text-white/60 font-bold mt-2'>{`Total de partidos: ${matches.length}`}</span>
            )}
            {!mainPool && startAt && (
              <span className='text-[12px] text-white/60 font-bold mt-1'>{`Inicio: ${getDayInMexico(startAt)} ${formatToMexicoUTC(startAt)}`}</span>
            )}
            {!mainPool && (
              <button
                className='text-[11px] flex text-white mt-5 mb-3 opacity-50 items-center underline z-30'
                onClick={() => setShowCreatePoolWizard(true)}>
                Agregar partido
                <img
                  src={addMatchImg}
                  alt='Add match'
                  className='object-contain w-[16px] ml-1'
                />
              </button>
            )}
          </section>
          <div className='flex flex-col w-[90%] flex-1 w-full items-center max-h-[65dvh] mt-2'>
            {!mainPool && (
              <div className='flex-1 overflow-scroll w-[100%] mb-8 min-h-[62dvh]'>
                {createPool()}
              </div>
            )}

            {matches.length > 5 && (
              <div className='flex justify-center w-full h-[90px] items-center'>
                <Button label='Crear' onClick={runCreateMainPool} />
              </div>
            )}

            {mainPool && (
              <div className='flex-1 overflow-scroll w-[100%] mb-8 min-h-[72dvh] mt-5'>
                {createPoolResults()}
              </div>
            )}
            {mainPool && (
              <div className='flex flex-grow mt-5 justify-center items-center'>
                {
                  <Button
                    label='Actualizar'
                    onClick={updatePool}
                    disabled={false}
                  />
                }
              </div>
            )}
          </div>
        </div>
      </AppContainer>
    </>
  );
};
